import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Paper, Tab, Tabs, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlug, faCheck, faLinkSlash, faChevronDown, faTrash, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { mcpScopeLabel, sortMcpScopes } from '../utils/mcpScopes';
import { mcpApi, mcpErrorKey } from '../utils/mcpApi';
import { McpIntro, McpLoading, McpPortalLayout } from '../components/McpUi';

function CopyField({ text, label }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  async function copy() {
    try { await navigator.clipboard.writeText(text); setStatus('mcp_copied'); }
    catch { setStatus('mcp_copy_failed'); }
    clearTimeout(timer.current); timer.current = setTimeout(() => setStatus(''), 4000);
  }
  return <div className="mcp-copy-field"><code tabIndex={0} aria-label={label}>{text}</code><Button size="small" onClick={copy} aria-label={`${t('mcp_copy')}: ${label}`} startIcon={<FontAwesomeIcon icon={status === 'mcp_copied' ? faCheck : faCopy} />}>{t(status === 'mcp_copied' ? status : 'mcp_copy')}</Button><span className="mcp-sr-only" role="status">{status && t(status)}</span>{status === 'mcp_copy_failed' && <Typography variant="caption" color="error">{t(status)}</Typography>}</div>;
}

// How to connect (Robert, 19.10.2026): a prompt the assistant can run itself, or the same steps by hand.
function ConnectionSetup({ resource }) {
  const { t } = useTranslation();
  const [client, setClient] = useState('codex');
  // Never interpolate an arbitrary URL into executable setup instructions.
  const commandUrl = /^https?:\/\/[A-Za-z0-9.:/[\]_%~-]+$/.test(resource);
  const codex = client === 'codex';
  const add = codex ? `codex mcp add ultimo-bots --url ${resource}` : `claude mcp add --transport http ultimo-bots ${resource}`;
  const name = codex ? 'Codex' : 'Claude Code';
  const steps = codex
    ? [{ text: 'mcp_setup_step_add', copy: add }, { text: 'mcp_setup_step_login_codex', copy: 'codex mcp login ultimo-bots' },
       { text: 'mcp_setup_step_approve' }, { text: 'mcp_setup_step_restart_codex' }, { text: 'mcp_setup_step_try' }]
    : [{ text: 'mcp_setup_step_add', copy: add }, { text: 'mcp_setup_step_login_claude' },
       { text: 'mcp_setup_step_approve' }, { text: 'mcp_setup_step_try' }];
  return <Paper variant="outlined" className="mcp-panel mcp-setup">
    <div className="mcp-panel-heading"><span className="mcp-icon-tile"><FontAwesomeIcon icon={faPlug} /></span><div><Typography component="h2" variant="h2">{t('mcp_connect_title')}</Typography><Typography variant="body2" color="text.secondary">{t('mcp_setup_intro')}</Typography></div></div>
    <Tabs value={client} onChange={(_, value) => setClient(value)} aria-label={t('mcp_connect_title')} sx={{ mb: 2 }}><Tab value="codex" label="Codex" id="mcp-tab-codex" aria-controls="mcp-setup-panel" /><Tab value="claude" label="Claude Code" id="mcp-tab-claude" aria-controls="mcp-setup-panel" /></Tabs>
    <div role="tabpanel" id="mcp-setup-panel" aria-labelledby={`mcp-tab-${client}`}>
      {commandUrl && <section className="mcp-setup-quick">
        <Typography component="h3" className="mcp-setup-title"><FontAwesomeIcon icon={faWandMagicSparkles} /> {t('mcp_setup_quick_title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('mcp_setup_quick_help', { client: name })}</Typography>
        <CopyField text={t(codex ? 'mcp_setup_prompt_codex' : 'mcp_setup_prompt_claude', { add })} label={t('mcp_setup_quick_title')} />
      </section>}
      <section className="mcp-setup-steps">
        <Typography component="h3" className="mcp-setup-title">{t('mcp_setup_steps_title')}</Typography>
        <ol className="mcp-steps">{steps.map((step, index) => <li key={step.text}>
          <span className="mcp-step-number" aria-hidden="true">{index + 1}</span>
          <div className="mcp-step-body"><Typography variant="body2">{t(step.text)}</Typography>{step.copy && commandUrl && <CopyField text={step.copy} label={t(step.text)} />}</div>
        </li>)}</ol>
      </section>
      <div className="mcp-setup-other"><Typography variant="caption" color="text.secondary">{t('mcp_setup_other')}</Typography><CopyField text={resource} label={t('mcp_server_url')} /></div>
    </div>
  </Paper>;
}

// A connection's permissions fold into one line (Robert, 19.10.2026); the chips open on demand.
function ConnectionScopes({ scopes, id }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const sorted = sortMcpScopes(scopes);
  const panel = `mcp-scopes-${id}`;
  return <div className="mcp-scope-fold">
    <Button className="mcp-scope-toggle" size="small" aria-expanded={open} aria-controls={panel} onClick={() => setOpen(value => !value)}
      endIcon={<FontAwesomeIcon icon={faChevronDown} className={open ? 'is-open' : ''} />}>
      <span>{t('mcp_permissions_summary', { count: sorted.length })}</span>
      <span className="mcp-scope-action">{t(open ? 'mcp_hide' : 'mcp_show')}</span>
    </Button>
    {open && <div className="mcp-scope-chips" id={panel}>{sorted.map(scope => <Chip key={scope} size="small" variant="outlined" label={mcpScopeLabel(scope, t)} />)}</div>}
  </div>;
}

export default function McpConnections() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [resource, setResource] = useState('');
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [removal, setRemoval] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const revocation = useRef(false);
  const date = value => new Date(value).toLocaleDateString(i18n.language);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    mcpApi('connections', { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) { setItems(data.connections); setCursor(data.next_cursor); setResource(data.resource_url); }
    }).catch(err => { if (!controller.signal.aborted) setError(mcpErrorKey(err)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [retry]);

  async function more() {
    setLoading(true); setError('');
    try {
      const data = await mcpApi(`connections?after=${encodeURIComponent(cursor)}`);
      setItems(current => [...current, ...data.connections]); setCursor(data.next_cursor);
    } catch (err) { setError(mcpErrorKey(err)); }
    finally { setLoading(false); }
  }

  async function revoke() {
    if (!confirm || revocation.current) return;
    revocation.current = true; setRevoking(true); setError('');
    try {
      await mcpApi(`connections/${confirm.id}`, { method: 'DELETE' });
      setItems(current => current.map(item => item.id === confirm.id ? { ...item, active: false } : item));
      setConfirm(null); setNotice('mcp_revoked_success');
    } catch (err) { setError(mcpErrorKey(err)); }
    finally { setRevoking(false); revocation.current = false; }
  }

  async function remove() {
    if (deleting || !removal) return;
    setDeleting(true); setError('');
    try {
      await mcpApi(`connections/${removal.id}/record`, { method: 'DELETE' });
      setItems(current => current.filter(item => item.id !== removal.id));
      setRemoval(null); setNotice('mcp_deleted_success');
    } catch (err) { setError(mcpErrorKey(err)); }
    finally { setDeleting(false); }
  }

  return <McpPortalLayout>
    <McpIntro title={t('mcp_connections_title')} subtitle={t('mcp_connections_intro')} />
    {error && !confirm && !removal && <Alert severity="error" sx={{ mb: 2 }} action={error !== 'mcp_unavailable' && <Button disabled={loading} onClick={() => cursor ? more() : setRetry(value => value + 1)}>{t('mcp_retry')}</Button>}>{t(error)}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{t(notice)}</Alert>}
    {resource && <ConnectionSetup resource={resource} />}
    <Typography component="h2" variant="h2" sx={{ mt: 3, mb: 2 }}>{t('mcp_connections_list')}</Typography>
    {!loading && !error && !items.length && <Paper variant="outlined" className="mcp-empty"><span className="mcp-icon-tile"><FontAwesomeIcon icon={faPlug} /></span><Typography variant="h2">{t('mcp_empty')}</Typography><Typography variant="body2" color="text.secondary">{t('mcp_empty_help')}</Typography></Paper>}
    <div className="mcp-connections">{items.map(item => <Paper component="article" variant="outlined" className="mcp-panel" key={item.id}>
      <div className="mcp-card-heading"><div className="mcp-panel-heading"><span className="mcp-icon-tile"><FontAwesomeIcon icon={faPlug} /></span><div><Typography component="h3" variant="h2">{item.client_name}</Typography><Typography variant="body2" color="text.secondary">{item.workspace_name}</Typography></div></div><Chip size="small" className={item.active ? 'mcp-active' : ''} label={t(item.active ? 'mcp_active' : 'mcp_inactive')} /></div>
      <ConnectionScopes scopes={item.scopes} id={item.id} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.agent_ids === null ? t('mcp_all_agents') : t('mcp_agents_count', { count: item.agent_ids.length })}</Typography>
      <Divider /><div className="mcp-card-footer"><Typography variant="caption" color="text.secondary">{t('mcp_expires', { date: date(item.expires_at) })}</Typography>{item.active
        ? <Button color="error" size="small" startIcon={<FontAwesomeIcon icon={faLinkSlash} />} disabled={revoking} onClick={() => { setError(''); setConfirm(item); }}>{t('mcp_revoke')}</Button>
        : <Button color="inherit" size="small" className="mcp-delete" startIcon={<FontAwesomeIcon icon={faTrash} />} disabled={deleting} onClick={() => { setError(''); setRemoval(item); }}>{t('mcp_delete')}</Button>}</div>
    </Paper>)}</div>
    {loading && <McpLoading />}
    {cursor && <Button disabled={loading} onClick={more}>{t('mcp_load_more')}</Button>}
    <Dialog open={!!confirm} onClose={() => { if (!revoking) { setConfirm(null); setError(''); } }} aria-labelledby="mcp-revoke-title" fullWidth maxWidth="xs">
      <DialogTitle id="mcp-revoke-title">{t('mcp_revoke')}</DialogTitle><DialogContent><Typography fontWeight={600} sx={{ mb: 1 }}>{confirm?.client_name}</Typography><DialogContentText>{t('mcp_revoke_confirm')}</DialogContentText>{error && <Alert severity="error" sx={{ mt: 2 }}>{t(error)}</Alert>}</DialogContent><DialogActions><Button disabled={revoking} onClick={() => { setConfirm(null); setError(''); }}>{t('mcp_cancel')}</Button><Button color="error" variant="contained" disabled={revoking} onClick={revoke}>{t(revoking ? 'mcp_loading' : 'mcp_revoke')}</Button></DialogActions>
    </Dialog>
    <Dialog open={!!removal} onClose={() => { if (!deleting) { setRemoval(null); setError(''); } }} aria-labelledby="mcp-delete-title" fullWidth maxWidth="xs">
      <DialogTitle id="mcp-delete-title">{t('mcp_delete_title')}</DialogTitle><DialogContent><Typography fontWeight={600} sx={{ mb: 1 }}>{removal?.client_name}</Typography><DialogContentText>{t('mcp_delete_confirm')}</DialogContentText>{error && <Alert severity="error" sx={{ mt: 2 }}>{t(error)}</Alert>}</DialogContent><DialogActions><Button disabled={deleting} onClick={() => { setRemoval(null); setError(''); }}>{t('mcp_cancel')}</Button><Button color="error" variant="contained" disabled={deleting} onClick={remove}>{t(deleting ? 'mcp_loading' : 'mcp_delete')}</Button></DialogActions>
    </Dialog>
  </McpPortalLayout>;
}
